import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import classnames from "classnames";

import useTranslate from "../hooks/use-translate.hook";
import { Chart, cssUtils } from "../ui";
import css from "./dashboard.scss";

const LossReasonsChart = ({
    className,
    onClickItem,
    initialDate,
    finalDate,
    users,
    statistics: { lost: { lossReasons } },
}) => {
    const t = useTranslate();

    const [ showHelp, setShowHelp ] = useState( false );
    const toggleHelp = () => setShowHelp( !showHelp );

    const series = useMemo( () => {
        let data = [];
        if ( lossReasons.length > 0 ) {
            data = lossReasons.map( lossReason => ({
                id: lossReason.id,
                name: lossReason.name,
                y: lossReason.total
            }));
        }

        return [{
            name: t( "dashboard:loss-reasons-chart.opportunities" ),
            colorByPoint: true,
            data
        }];
    }, [ t, lossReasons ] );

    const data = useMemo( () => {
        return {
            chart: {
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
            },
            title: {
                text: t( "dashboard:loss-reasons-chart.title" )
            },
            exporting: {
                csv: {
                    columnHeaderFormatter: function ( item ) {
                        if ( item?.name ) {
                            return item.name;
                        }
                        return t( "dashboard:loss-reasons-chart.title" );
                    }
                },
                chartOptions: {
                    plotOptions: {
                        pie: {
                            dataLabels: {
                                enabled: true,
                                format: "<b>{point.name}</b>: {point.percentage:.1f} %"
                            }
                        }
                    }
                }
            },
            tooltip: {
                pointFormat: "{series.name}: <b>{point.y} - {point.percentage:.1f}%</b>"
            },
            plotOptions: {
                pie: {
                    dataLabels: {
                        enabled: false
                    },
                    showInLegend: true
                },
                series: {
                    cursor: "pointer",
                    point: {
                        events: {
                            click: function () {
                                if ( !this.id ) {
                                    return;
                                }

                                onClickItem(
                                    t(
                                        "dashboard:loss-reasons-chart.title-dialog",
                                        { lossReason: this.name.toLowerCase() }
                                    ),
                                    {
                                        initialLostAt: initialDate,
                                        finalLostAt: finalDate,
                                        lossReasonId: this.id,
                                        status: "LOST",
                                        users: users.toString()
                                    }
                                );
                            }
                        }
                    }
                }
            },
            series,
        };
    }, [ t, series, onClickItem, initialDate, finalDate, users ] );

    return (
        <Card className={ classnames( className, cssUtils.positionRelative ) }>
            <Dialog
                open={ showHelp }
                onClose={ toggleHelp }
            >
                <DialogTitle>{ t( "dashboard:help" ) }</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        { t( "dashboard:loss-reasons-chart.help" ) }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={ toggleHelp }>{ "OK "}</Button>
                </DialogActions>
            </Dialog>

            <Chart
                id="opportunitiesLostReasonsChart"
                data={ data }
                noDataMessage={ t( "dashboard:loss-reasons-chart.empty" ) }
                type="pie"
            />

            <Tooltip title={ t( "dashboard:help" ) }>
                <IconButton className={ css.iconHelpChart } onClick={ toggleHelp } size="large">
                    <InfoIcon/>
                </IconButton>
            </Tooltip>
        </Card>
    );
};

export default LossReasonsChart;